import {
  ProvinceData,
  useProvinceBulkDelete,
  useProvinces,
  useExportProvince,
} from "@/api/province";
import { TableDefault } from "@/components/table/table-default";
import { TableForm } from "@/components/table/table-form";
import {
  DataConfig,
  useDefaultDataConfig,
} from "@/components/table/useDefaultDataConfig";
import { generateCustomSpec } from "@/utils/helper";
import {
  swalDeleteWithList,
  swalError,
  swalSuccess,
  swalErrorDefault,
} from "@/utils/lib/swal";
import { TableInstance } from "alurkerja-ui/dist/types";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { ROLE } from "@/utils/constant";
import { useCurrentUser } from "../auth/useCurrentUser";
import { DownloadButton } from "@/components/ui/button.tsx";
import { nullElement } from "@/components/null-element";

const customAdminSpec = generateCustomSpec({
  path: "/api/crud/province",
  fields: [
    {
      label: "Province Name",
      name: "provinceName",
      searchable: true,
      sortable: true,
    },
    {
      label: "Province Code",
      name: "provinceCode",
      searchable: true,
      sortable: true,
    },
  ],
});

const customNonAdminSpec = generateCustomSpec({
  path: "/api/crud/province",
  canBulk: false,
  fields: [
    {
      label: "Province Name",
      name: "provinceName",
      searchable: true,
      sortable: true,
    },
    {
      label: "Province Code",
      name: "provinceCode",
      searchable: true,
      sortable: true,
    },
  ],
});

const HeaderElement = ({ dataConfig }: { dataConfig: DataConfig }) => {
  const { mutate: exportList, isPending: isDownloading } = useExportProvince();

  const handleExport = () => {
    const params = {
      ...dataConfig,
      limit: undefined,
      page: undefined,
    };

    exportList(
      {
        config: params,
      },
      {
        onError: () => {
          swalErrorDefault.fire();
        },
      },
    );
  };
  return (
    <div className="flex items-center justify-end gap-2">
      <DownloadButton onClick={handleExport} loading={isDownloading} />
    </div>
  );
};

export function ProvinceTable() {
  const [selectedRow, setSelectedRow] = useState<number[]>([]);
  const { dataConfig, setDataConfig } = useDefaultDataConfig();
  const tableForm = useForm();
  const ref = useRef<TableInstance>(null);

  const { data: provinces } = useProvinces(dataConfig);
  const { mutate, isPending } = useProvinceBulkDelete();

  const { roles } = useCurrentUser();
  const isValidator = roles.includes(ROLE.VALIDATOR_DOCUMENT_OFFICER);
  const isSysAdminView = roles.includes(ROLE.SYS_ADMIN_VIEW);

  function handleBulkDelete() {
    const provinces = (ref.current?.getData() as ProvinceData[]) ?? [];
    const selectedProvinces = provinces.filter((province) =>
      (selectedRow as unknown as string[]).includes(province.id),
    );
    const selecteds = selectedProvinces.map(
      (province) => `${province.provinceCode} - ${province.provinceName}`,
    );

    swalDeleteWithList({
      items: selecteds,
      preConfirm: () => {
        mutate(
          { ids: selectedRow as unknown[] as string[] },
          {
            onSuccess: () => {
              swalSuccess.fire({
                text: "Province has been deleted",
              });
            },
            onError: (error) => {
              swalError.fire({
                text: error.message,
              });
            },
            onSettled: () => {
              setSelectedRow([]);
            },
          },
        );
      },
    });
  }

  return (
    <>
      <TableForm
        setDataConfig={setDataConfig}
        form={tableForm}
        createPath={
          isValidator || isSysAdminView ? null : "/master-data/province/create"
        }
        selectedIds={selectedRow as unknown as string[]}
        handleBulkDelete={
          isValidator || isSysAdminView ? null : handleBulkDelete
        }
        isPendingDelete={isPending}
      />
      <TableDefault
        ref={ref}
        spec={isValidator ? customNonAdminSpec : customAdminSpec}
        data={provinces}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
        dataConfig={dataConfig}
        setDataConfig={setDataConfig}
        searchFilterByColumns={true}
        headerElement={<HeaderElement dataConfig={dataConfig} />}
        customButtonEdit={isSysAdminView ? nullElement : undefined}
      />
    </>
  );
}
